import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { APP_STORE_URL } from '../constants/storeUrls';
import {
  trackEvent,
  WEB_DOWNLOAD_PAGE_VIEW,
  WEB_REFERRAL_COPY,
  WEB_STORE_CLICK,
} from '../lib/analytics';
import { useSEO } from '../hooks/useSEO';
import styles from './Download.module.css';

export function Download() {
  const [searchParams] = useSearchParams();
  const ref = (searchParams.get('ref') || '').trim().toUpperCase();
  const [copied, setCopied] = useState(false);

  useSEO({
    title: ref ? `Download LuvLust — code ${ref}` : 'Download LuvLust',
    description: 'Get LuvLust for iPhone. The #1 app for couples.',
  });

  const shareableLink = useMemo(() => {
    if (!ref) return '';
    return `https://luvlustapp.com/r/${ref}`;
  }, [ref]);

  useEffect(() => {
    void trackEvent(WEB_DOWNLOAD_PAGE_VIEW, { ref_code: ref || undefined });
  }, [ref]);

  function copyCode() {
    if (!ref) return;
    void navigator.clipboard.writeText(ref);
    void trackEvent(WEB_REFERRAL_COPY, { type: 'code', ref_code: ref });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function copyShareLink() {
    if (!shareableLink) return;
    void navigator.clipboard.writeText(shareableLink);
    void trackEvent(WEB_REFERRAL_COPY, { type: 'link', ref_code: ref });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleStoreClick() {
    void trackEvent(WEB_STORE_CLICK, {
      platform: 'ios',
      location: 'download_page',
      ref_code: ref || undefined,
    });
  }

  return (
    <div className={styles.wrap}>
      <h1 className={styles.title}>Download LuvLust</h1>
      <p className={styles.lead}>
        Download LuvLust on the App Store. If a friend shared this page with you, use their referral code after
        you install (the app may prompt you, or you can enter it in settings when available).
      </p>

      {ref ? (
        <>
          <div className={styles.codeBlock}>
            <span className={styles.code}>Referral code: {ref}</span>
            <button type="button" className={styles.copyBtn} onClick={copyCode}>
              {copied ? 'Copied!' : 'Copy code'}
            </button>
          </div>
          <p className={styles.hint} style={{ marginTop: '-12px', marginBottom: '20px' }}>
            <strong>Partners:</strong> use your tracking link{' '}
            <code style={{ fontSize: '0.85em' }}>{shareableLink}</code>{' '}
            <button type="button" className={styles.copyBtn} style={{ marginLeft: 8 }} onClick={copyShareLink}>
              Copy tracking link
            </button>
          </p>
        </>
      ) : null}

      <div className={styles.stores}>
        <a
          className={`${styles.storeLink} ${styles.storeLinkIos}`}
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleStoreClick}
        >
          Download on the App Store
        </a>
      </div>

      <p className={styles.hint}>
        LuvLust is currently available on iPhone. Android is coming back soon.
      </p>
    </div>
  );
}
